/**
 * BB-QUEUE.JS - Queue System Module
 * Firebase-based gatekeeper for controlled access
 * Supports "open mode" when Firebase is not configured
 * 
 * @version 1.0.0
 * @requires BB.Player for user identity
 */

window.BB = window.BB || {};

window.BB.Queue = (function(Player) {
  'use strict';
  
  var VERSION = '1.0.0';
  var firebaseApp, database;
  var isInitialized = false;
  var myTicket = null;
  var isReleased = true; // DEFAULT TO TRUE (open mode) until Firebase is configured
  var queuePosition = 0;
  var isOpenMode = true; // No Firebase = open mode
  var callbacks = {
    onRelease: [],
    onPositionChange: []
  };
  
  // Firebase config - REPLACE WITH YOUR OWN
  // When these are placeholder values, the system runs in "open mode" (everyone can enter)
  var firebaseConfig = {
    apiKey: "YOUR_FIREBASE_API_KEY",
    authDomain: "YOUR_PROJECT.firebaseapp.com",
    databaseURL: "https://YOUR_PROJECT-default-rtdb.firebaseio.com",
    projectId: "YOUR_PROJECT",
    storageBucket: "YOUR_PROJECT.appspot.com",
    messagingSenderId: "YOUR_SENDER_ID",
    appId: "YOUR_APP_ID"
  };
  
  // ========================================================================
  // INITIALIZATION
  // ========================================================================
  
  async function init() {
    if (isInitialized) return;
    
    // Check if Firebase is configured (not placeholder values)
    if (firebaseConfig.apiKey === "YOUR_FIREBASE_API_KEY" || 
        firebaseConfig.projectId === "YOUR_PROJECT") {
      console.log('🚪 [BB.Queue] Running in OPEN MODE (Firebase not configured)');
      isOpenMode = true;
      isReleased = true;
      isInitialized = true;
      triggerCallbacks('onRelease');
      return;
    }
    
    try {
      // Dynamic import Firebase
      var firebase = await import('https://www.gstatic.com/firebasejs/10.7.1/firebase-app.js');
      var firebaseDb = await import('https://www.gstatic.com/firebasejs/10.7.1/firebase-database.js');
      
      isOpenMode = false;
      isReleased = false; // Now we need to check Firebase for release status
      
      // Initialize Firebase
      firebaseApp = firebase.initializeApp(firebaseConfig);
      database = firebaseDb.getDatabase(firebaseApp);
      
      // Get or create ticket
      myTicket = getOrCreateTicket();
      
      // Register in queue
      await registerInQueue(firebaseDb);
      
      // Subscribe to release status
      subscribeToRelease(firebaseDb);
      
      // Subscribe to queue position
      subscribeToQueuePosition(firebaseDb);
      
      isInitialized = true;
      console.log('🎟️ [BB.Queue] Initialized with ticket', myTicket.substring(0, 8) + '...');
      
    } catch (e) {
      console.warn('🔴 [BB.Queue] Firebase init failed, running in open mode', e);
      // Fallback: Allow entry without queue
      isReleased = true;
      triggerCallbacks('onRelease');
    }
  }
  
  // ========================================================================
  // TICKET MANAGEMENT
  // ========================================================================
  
  function getOrCreateTicket() {
    var stored = localStorage.getItem('bb_queue_ticket');
    if (stored) return stored;
    
    var newTicket = 'ticket_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('bb_queue_ticket', newTicket);
    return newTicket;
  }
  
  async function registerInQueue(firebaseDb) {
    var queueRef = firebaseDb.ref(database, 'queue/' + myTicket);
    
    // Get player info from Player module
    var playerName = Player ? Player.getName() : 'Anonymous';
    var playerColor = Player ? Player.getColor() : { bg: '#ff6b6b', text: '#ffffff' };
    var playerSprite = Player ? Player.getSprite() : '🃏';
    
    await firebaseDb.set(queueRef, {
      name: playerName,
      joinedAt: Date.now(),
      color: playerColor,
      sprite: playerSprite
    });
    
    // Set up disconnect cleanup
    firebaseDb.onDisconnect(queueRef).remove();
  }
  
  // ========================================================================
  // FIREBASE SUBSCRIPTIONS
  // ========================================================================
  
  function subscribeToRelease(firebaseDb) {
    var releaseRef = firebaseDb.ref(database, 'released/' + myTicket);
    firebaseDb.onValue(releaseRef, function(snapshot) {
      if (snapshot.exists()) {
        isReleased = true;
        console.log('🎉 [BB.Queue] You have been released!');
        triggerCallbacks('onRelease');
      }
    });
  }
  
  function subscribeToQueuePosition(firebaseDb) {
    var queueRef = firebaseDb.ref(database, 'queue');
    firebaseDb.onValue(queueRef, function(snapshot) {
      if (!snapshot.exists()) {
        queuePosition = 0;
        triggerCallbacks('onPositionChange', 0);
        return;
      }
      
      var queue = snapshot.val();
      var tickets = Object.keys(queue);
      var myData = queue[myTicket];
      if (!myData) {
        queuePosition = 0;
        triggerCallbacks('onPositionChange', 0);
        return;
      }
      
      // Sort by joinedAt to get position
      var sorted = tickets.sort(function(a, b) {
        return queue[a].joinedAt - queue[b].joinedAt;
      });
      
      var position = sorted.indexOf(myTicket);
      queuePosition = position;
      triggerCallbacks('onPositionChange', position);
    });
  }
  
  // ========================================================================
  // CALLBACKS
  // ========================================================================
  
  function triggerCallbacks(type, data) {
    callbacks[type].forEach(function(cb) {
      try { cb(data); } catch(e) {}
    });
  }
  
  function onRelease(callback) {
    callbacks.onRelease.push(callback);
    // Immediately call if already released
    if (isReleased) callback();
  }
  
  function onPositionChange(callback) {
    callbacks.onPositionChange.push(callback);
    callback(queuePosition);
  }
  
  // ========================================================================
  // GETTERS
  // ========================================================================
  
  function getTicket() { return myTicket; }
  function getIsReleased() { return isReleased; }
  function getPosition() { return queuePosition; }
  function getIsOpenMode() { return isOpenMode; }
  
  // ========================================================================
  // PERSISTENCE
  // ========================================================================
  
  // Check if user was previously released (skip the queue)
  function wasAlreadyReleased() {
    return localStorage.getItem('bb_queue_released') === 'true';
  }
  
  function markAsReleased() {
    localStorage.setItem('bb_queue_released', 'true');
  }
  
  // ========================================================================
  // FIREBASE CONFIG UPDATE
  // Allow runtime configuration (e.g., from theme settings)
  // ========================================================================
  
  function configure(newConfig) {
    if (isInitialized) {
      console.warn('[BB.Queue] Cannot reconfigure after initialization');
      return;
    }
    Object.assign(firebaseConfig, newConfig);
  }
  
  console.log('[BB.Queue] Module loaded v' + VERSION);
  
  // ========================================================================
  // PUBLIC API
  // ========================================================================
  
  return {
    version: VERSION,
    init: init,
    configure: configure,
    getTicket: getTicket,
    getIsReleased: getIsReleased,
    getPosition: getPosition,
    getIsOpenMode: getIsOpenMode,
    onRelease: onRelease,
    onPositionChange: onPositionChange,
    wasAlreadyReleased: wasAlreadyReleased,
    markAsReleased: markAsReleased
  };
})(window.BB.Player);

// Backwards compatibility alias
window.QueueSystem = window.BB.Queue;

